import axios from 'axios';

import { Constants } from '@/constants';

const path = (userId: number) =>
  `${Constants.apiPath}/users/${userId}/data-management`;

export type DataType = 'inventory' | 'sessions';
export interface ImportHumidor {
  id: number;
  name: string;
  is_default: boolean;
}
export interface ImportRow {
  id?: number;
  row_number: number;
  source_key: string;
  source: Record<string, string>;
  normalized: Record<string, any>;
  status: string;
  cigar_id: number | null;
  suggested_cigar_id: number | null;
  confidence: number | null;
  record_id: number | null;
  error: string | null;
}
export interface ImportJob {
  id: string;
  filename: string;
  data_type: DataType;
  status: string;
  created_at: string;
  completed_at: string | null;
  unresolved?: number;
  ai_usage?: AiUsage;
}
export interface AiUsage {
  billing_mode: 'platform' | 'user_credits' | 'mixed';
  credits_charged: number;
  requests: number;
  failed_requests: number;
  retry_requests?: number;
  input_tokens: number;
  output_tokens: number;
  cached_input_tokens: number;
  estimated_cost_usd?: number;
  actual_cost_usd?: number;
  total_cost_usd: number;
  retry_cost_usd?: number;
  estimated_requests: number;
  breakdown?: Array<{
    operation: string;
    provider: string;
    model: string;
    requests: number;
    input_tokens: number;
    output_tokens: number;
    total_cost_usd: number;
    estimated_requests: number;
  }>;
}
export interface MatchGroup {
  group_key: string;
  brand: string;
  cigar_name: string;
  vitola: string;
  suggested_cigar_id: number | null;
  confidence: number | null;
  affected_records: number;
  status: string;
}

export const getDataSummary = async (userId: number) =>
  (
    await axios.get<{
      humidors: number;
      inventory_records: number;
      total_quantity: number;
      sessions: number;
    }>(`${path(userId)}/summary`)
  ).data;
export const getDataHumidors = async (userId: number) =>
  (await axios.get<ImportHumidor[]>(`${path(userId)}/humidors`)).data;
export const previewDataImport = async (
  userId: number,
  payload: {
    dataType: DataType;
    filename: string;
    csv: string;
    mapping: Record<string, string>;
  }
) => (await axios.post(`${path(userId)}/preview`, payload)).data;
export const stageDataImport = async (
  userId: number,
  payload: {
    dataType: DataType;
    filename: string;
    csv: string;
    mapping: Record<string, string>;
    creditClassification?: 'customer' | 'promotional';
    destination?:
      | { mode: 'default' }
      | { mode: 'existing'; humidorId: number }
      | { mode: 'new' };
  }
) =>
  (await axios.post<{ jobId: string }>(`${path(userId)}/imports`, payload))
    .data;
export const executeDataImport = async (
  userId: number,
  jobId: string,
  retryFailed = false
) =>
  (
    await axios.post(`${path(userId)}/imports/${jobId}/execute`, {
      confirmTargetUserId: userId,
      retryFailed,
    })
  ).data;
export const listDataImports = async (userId: number) =>
  (await axios.get<ImportJob[]>(`${path(userId)}/imports`)).data;
export const getCumulativeImportAiUsage = async (userId: number) =>
  (await axios.get<AiUsage>(`${path(userId)}/imports/ai-usage`)).data;
export const getDataImport = async (userId: number, jobId: string) =>
  (
    await axios.get<{
      job: ImportJob;
      rows: ImportRow[];
      counts: Array<{ status: string; count: number }>;
      progress: {
        total: number;
        finished: number;
        deterministic_imported: number;
        ai_imported: number;
        research_pending: number;
        awaiting_credits: number;
      };
      reconciliation: { total_groups: number; reviewed_groups: number };
      ai_usage: AiUsage;
    }>(`${path(userId)}/imports/${jobId}`)
  ).data;
export const listImportMatches = async (userId: number, jobId: string) =>
  (await axios.get<MatchGroup[]>(`${path(userId)}/imports/${jobId}/matches`))
    .data;
export const searchImportCigars = async (userId: number, query: string) =>
  (
    await axios.get<
      Array<{
        cigar_id: number;
        brand: string;
        name: string;
        wrapper: string | null;
        origin: string | null;
        image_uri: string | null;
        image_url: string | null;
        vitolas: string | null;
      }>
    >(`${path(userId)}/cigars`, { params: { q: query } })
  ).data;
export const applyImportMatch = async (
  userId: number,
  jobId: string,
  groupKey: string,
  cigarId: number
) =>
  (
    await axios.post(`${path(userId)}/imports/${jobId}/matches/${groupKey}`, {
      cigarId,
    })
  ).data;
export const skipImportMatch = async (
  userId: number,
  jobId: string,
  groupKey: string
) =>
  (
    await axios.post(
      `${path(userId)}/imports/${jobId}/matches/${groupKey}/skip`
    )
  ).data;
export const downloadUserData = async (
  userId: number,
  inventory: boolean,
  sessions: boolean
) =>
  (
    await axios.get<Blob>(`${path(userId)}/export`, {
      params: { inventory, sessions },
      headers: { Accept: 'application/zip' },
      responseType: 'blob',
    })
  ).data;
export const downloadImportReport = async (
  userId: number,
  jobId: string,
  status:
    | 'all'
    | 'imported'
    | 'failed'
    | 'unmatched'
    | 'review'
    | 'skipped'
    | 'pending' = 'all'
) =>
  (
    await axios.get<Blob>(`${path(userId)}/imports/${jobId}/report`, {
      params: { status },
      responseType: 'blob',
    })
  ).data;
